import React, { useState, useRef, useEffect, useMemo } from "react";
import { Form, Button, InputGroup, Row, Col, Spinner, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  addQuickReply,
  getFrontEndQuickReply,
  updateQuickReply,
  uploadFileInChat,
} from "../../reducers/commonReducer";
import { socketEmit } from "../../Socket";
import socket from "../../socketInstance";
import { useLocation, useNavigate } from "react-router-dom";
import ReplyPreview from "./ReplyPreview";

const ChatInput = ({ id, groupId, isGroupChat, chatMessages, setChatMessages, replyMessage, setReplyMessage, socketReady, mentionMembers = [] }) => {
  const myId = localStorage.getItem("userid");

  // Build mention list from prop (all group members via API) OR fall back to unique senders in loaded messages
  const effectiveMentionMembers = useMemo(() => {
    if (!isGroupChat) return [];
    if (mentionMembers.length > 0) return mentionMembers;
    const seen = new Set();
    const derived = [];
    for (const msg of chatMessages) {
      const senderId = msg?.sender?.id || msg?.sender?._id || (typeof msg?.sender === "string" ? msg.sender : null);
      const senderName = msg?.senderName || msg?.sender?.username || "";
      if (senderId && senderId !== myId && senderName && !seen.has(senderId)) {
        seen.add(senderId);
        derived.push({ id: senderId, name: senderName });
      }
    }
    return derived;
  }, [isGroupChat, mentionMembers, chatMessages, myId]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [attachmentPreviews, setAttachmentPreviews] = useState([]);
  const [fileupload, setFileupload] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [audioPreview, setAudioPreview] = useState(null);
  const [emoji, setEmoji] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showSlashReplies, setShowSlashReplies] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionHighlightIdx, setMentionHighlightIdx] = useState(0);
  const [showStickerManager, setShowStickerManager] = useState(false);
  const [savingSticker, setSavingSticker] = useState(false);
  const [stickerTitle, setStickerTitle] = useState("");
  const [stickerFile, setStickerFile] = useState(null);
  const [stickerPreviewUrl, setStickerPreviewUrl] = useState("");
  const mediaRecorderRef = useRef(null);
  const chunks = useRef([]);
  const inputRef = useRef(null);
  const audioStreamRef = useRef(null);
  const userSlug = localStorage.getItem("slug");
  const canUseQuickReplies = userSlug !== "customer";
  const isTeamLeader = userSlug === "team_leader";

  // Wait for socket to reconnect, then retry sending the payload
  const sendWithRetry = (payload) => {
    return new Promise((resolve, reject) => {
      if (socket.connected) {
        resolve(socketEmit("send_message", payload));
        return;
      }
      // Wait up to 30s for reconnection then retry once
      const timer = setTimeout(() => {
        socket.off("connect", onReconnect);
        reject(new Error("Could not reconnect within 30 seconds"));
      }, 30000);
      const onReconnect = () => {
        clearTimeout(timer);
        // Give save_connection a moment to register on the server
        setTimeout(() => resolve(socketEmit("send_message", payload)), 800);
      };
      socket.once("connect", onReconnect);
    });
  };

  // Fetch emojis
  useEffect(() => {
    if (canUseQuickReplies) {
      dispatch(getFrontEndQuickReply());
    }
  }, [canUseQuickReplies, dispatch]);

  useEffect(() => {
    if (replyMessage) {
      inputRef.current?.focus();
    }
  }, [replyMessage]);

  //localstorage Clear
  useEffect(() => {
    localStorage.removeItem("chatMessages");
  }, [location.pathname]);
  const { getFrontEndQuickReplyList } = useSelector(
    (state) => state.commonReducer
  );

  const stickerItems = useMemo(() => {
    return (getFrontEndQuickReplyList || [])
      .filter((item) => item?.messageType === "sticker" && item?.files?.length > 0)
      .sort((firstItem, secondItem) => {
        const firstOrder = Number(firstItem?.orderBy || 0);
        const secondOrder = Number(secondItem?.orderBy || 0);
        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return new Date(secondItem?.updatedAt || secondItem?.createdAt || 0) -
          new Date(firstItem?.updatedAt || firstItem?.createdAt || 0);
      });
  }, [getFrontEndQuickReplyList]);

  const slashReplyItems = useMemo(() => {
    return (getFrontEndQuickReplyList || []).filter(
      (item) => item?.messageType !== "sticker"
    );
  }, [getFrontEndQuickReplyList]);

  useEffect(() => {
    if (!stickerFile) {
      setStickerPreviewUrl("");
      return undefined;
    }

    const previewUrl = URL.createObjectURL(stickerFile);
    setStickerPreviewUrl(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [stickerFile]);

  // Handle text message change
  const handleChange = (e) => {
    const nextMessage = e.target.value;
    const cursorPos = e.target.selectionStart;
    setMessage(nextMessage);
    // Auto-resize textarea
    const el = e.target;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
    setShowSlashReplies(
      canUseQuickReplies && nextMessage.trimStart().startsWith("/")
    );
    // Detect @mention trigger
    if (effectiveMentionMembers.length > 0) {
      const textBeforeCursor = nextMessage.slice(0, cursorPos);
      const atIdx = textBeforeCursor.lastIndexOf("@");
      if (atIdx !== -1) {
        const between = textBeforeCursor.slice(atIdx + 1);
        // Ignore if this @ is inside an already-inserted @[name] tag
        const alreadyTagged = /^[^\[]*\]/.test(between);
        if (!alreadyTagged && !between.includes(" ") && !between.includes("\n")) {
          setMentionQuery(between);
          setMentionStartIndex(atIdx);
          setShowMentions(true);
          setMentionHighlightIdx(0);
          return;
        }
      }
      setShowMentions(false);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const plain = e.clipboardData.getData("text/plain");
    const input = e.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const next = input.value.slice(0, start) + plain + input.value.slice(end);
    setMessage(next);
    requestAnimationFrame(() => {
      input.style.height = "auto";
      input.style.height = Math.min(input.scrollHeight, 120) + "px";
      input.setSelectionRange(start + plain.length, start + plain.length);
    });
  };

  const filteredMentions = effectiveMentionMembers.filter((m) =>
    m.name.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  const insertMention = (member) => {
    const input = inputRef.current;
    const cursorPos = input ? input.selectionStart : mentionStartIndex + mentionQuery.length + 1;
    const before = message.slice(0, mentionStartIndex);
    const after = message.slice(cursorPos);
    const tag = `@[${member.name}]`;
    const newMsg = `${before}${tag} ${after}`;
    setMessage(newMsg);
    setShowMentions(false);
    setMentionQuery("");
    setMentionStartIndex(-1);
    const newCursorPos = mentionStartIndex + tag.length + 1;
    requestAnimationFrame(() => {
      if (input) {
        input.focus();
        input.style.height = "auto";
        input.style.height = Math.min(input.scrollHeight, 120) + "px";
        input.setSelectionRange(newCursorPos, newCursorPos);
      }
    });
  };

  const handleInputKeyDown = (e) => {
    if (showMentions && filteredMentions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionHighlightIdx((prev) => (prev + 1) % filteredMentions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionHighlightIdx((prev) => (prev - 1 + filteredMentions.length) % filteredMentions.length);
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertMention(filteredMentions[mentionHighlightIdx]);
        return;
      }
      if (e.key === "Escape") {
        setShowMentions(false);
        return;
      }
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const EMOJI_LIST = [
    "😍","🤨","✅","❌","❤️",
    "👍","😊","😂","🙏","👏",
    "🔥","😢","😠","🤝","⚡",
  ];

  const insertEmoji = (em) => {
    const input = inputRef.current;
    if (!input) { setMessage((prev) => prev + em); return; }
    const start = input.selectionStart ?? message.length;
    const end = input.selectionEnd ?? message.length;
    const next = message.slice(0, start) + em + message.slice(end);
    setMessage(next);
    setShowEmojiPicker(false);
    requestAnimationFrame(() => {
      input.focus();
      input.style.height = "auto";
      input.style.height = Math.min(input.scrollHeight, 120) + "px";
      input.setSelectionRange(start + em.length, start + em.length);
    });
  };
  // Handle image upload
  // const handleImageChange = (e) => {
  //   const files = Array.from(e.target.files); 
  //   if (files.length > 0) {
  //     setFileupload((prev) => [...prev, files]);
  //     const imageUrls = files.map(file => URL.createObjectURL(file));
  //     setImages((prev) => [...prev, { url: blobUrl, type: file.type }]);
  //   }
  // };
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setFileupload((prev) => [...prev, ...files]); 
      const newPreviews = files.map((file) => ({
        id: `${file.name}-${file.lastModified}-${Math.random()}`,
        url: URL.createObjectURL(file),
        type: file.type,
        file,
      }));
      setAttachmentPreviews((prev) => [...prev, ...newPreviews]); 
    }
  };
  
  const handleDeleteAttachment = (previewId) => {
    setAttachmentPreviews((prev) => {
      const previewToDelete = prev.find((item) => item.id === previewId);
      if (previewToDelete?.url) {
        URL.revokeObjectURL(previewToDelete.url);
      }

      if (previewToDelete?.file) {
        setFileupload((current) =>
          current.filter((file) => file !== previewToDelete.file)
        );
      }

      return prev.filter((item) => item.id !== previewId);
    });
  };

  const clearAudioPreview = () => {
    if (audioPreview?.url) {
      URL.revokeObjectURL(audioPreview.url);
    }

    setFileupload((prev) =>
      prev.filter((file) => !file?.type?.startsWith("audio/"))
    );

    setAudioPreview(null);
  };

  const replacePendingAudioFile = (nextAudioFile, nextAudioUrl) => {
    if (audioPreview?.url) {
      URL.revokeObjectURL(audioPreview.url);
    }

    setFileupload((prev) => {
      const withoutPreviousAudio = prev.filter(
        (file) => !file?.type?.startsWith("audio/")
      );

      return [...withoutPreviousAudio, nextAudioFile];
    });

    setAudioPreview({
      file: nextAudioFile,
      url: nextAudioUrl,
    });
  };

  const getAudioRecorderMimeType = () => {
    const mimeCandidates = [
      "audio/mp4",
      "audio/aac",
      "audio/ogg;codecs=opus",
      "audio/ogg",
      "audio/webm;codecs=opus",
      "audio/webm",
    ];

    const supportedMimeType = mimeCandidates.find((type) =>
      MediaRecorder.isTypeSupported(type)
    );

    return supportedMimeType || "";
  };

  const detectMessageType = (files) => {
    if (!files?.length) {
      return "text";
    }

    if (files.every((file) => file.type?.startsWith("audio/"))) {
      return "audio";
    }

    if (files.every((file) => file.type?.startsWith("image/"))) {
      return "image";
    }

    if (files.every((file) => file.type?.startsWith("video/"))) {
      return "video";
    }

    return "file";
  };

  const getFilesForUpload = (files) => {
    const normalizedFiles = files.filter((file) => file instanceof File);
    const uniqueFiles = normalizedFiles.filter(
      (file, index, self) =>
        index ===
        self.findIndex(
          (current) =>
            current.name === file.name &&
            current.size === file.size &&
            current.lastModified === file.lastModified &&
            current.type === file.type
        )
    );

    if (uniqueFiles.every((file) => file.type?.startsWith("audio/"))) {
      return uniqueFiles.slice(-1);
    }

    return uniqueFiles;
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices || !window.MediaRecorder) {
        alert("Your browser doesn't support audio recording.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;

      const mimeType = getAudioRecorderMimeType();
      mediaRecorderRef.current = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const finalMimeType = mimeType || mediaRecorderRef.current?.mimeType || "audio/webm";
        const extension = finalMimeType.includes("mp4")
          ? "m4a"
          : finalMimeType.includes("ogg")
          ? "ogg"
          : "webm";
        const blob = new Blob(chunks.current, { type: finalMimeType });
        const audioFile = new File([blob], `recording_${Date.now()}.${extension}`, {
          type: finalMimeType,
        });
        const previewUrl = URL.createObjectURL(blob);

        replacePendingAudioFile(audioFile, previewUrl);
        chunks.current = [];
        audioStreamRef.current?.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error starting recording:", error);
      audioStreamRef.current?.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
      alert("Microphone access denied or not supported.");
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };
  //get User Chat
  const fetchUsers = async () => {
        try {
          const response = await socketEmit("older_messages", isGroupChat ? { groupid: groupId } : { userid : id });
          console.log("✅ older_messages", response);
          setChatMessages(response?.data || []);
        } catch (err) {
          console.error("Socket emit error:", err);
        } finally {
          setLoading(false);
        }
      };

  const clearComposer = () => {
    setMessage("");
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
    }
    setReplyMessage(null);
    setFileupload([]);
    setAttachmentPreviews((prev) => {
      prev.forEach((item) => item.url && URL.revokeObjectURL(item.url));
      return [];
    });

    if (audioPreview?.url) {
      URL.revokeObjectURL(audioPreview.url);
    }

    setAudioPreview(null);
  };

  const normalizeOutgoingMessage = (serverMessage) => {
    if (!serverMessage) {
      return serverMessage;
    }

    if (serverMessage?.replyTo || !replyMessage) {
      return serverMessage;
    }

    return {
      ...serverMessage,
      replyTo: replyMessage,
    };
  };

  const getAttachmentIds = (item) =>
    item?.files?.map((file) => file?.id || file?._id).filter(Boolean) || [];

  const sendQuickReplyItem = async (item) => {
    setLoading(true);
    const attachmentIds = getAttachmentIds(item);
    const extendedPayload = {
      attachments: attachmentIds,
      messageType: "text",
      content: item?.content || "",
    };

    if (isGroupChat) {
      extendedPayload.groupid = groupId;
    } else {
      extendedPayload.touserid = id;
      extendedPayload.recipient = id;
    }

    try {
      const parsedResponse = await sendWithRetry(extendedPayload);
      if (parsedResponse?.status === 1) {
        setChatMessages((prev) => {
          const updated = [...prev, normalizeOutgoingMessage(parsedResponse?.data)];
          localStorage.setItem("chatMessages", JSON.stringify(updated));
          return updated;
        });
        setMessage("");
        setShowSlashReplies(false);
        setEmoji(false);
        setLoading(false);
        if (!isGroupChat) {
          fetchUsers();
        }
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error("Error during socket emit:", error);
      setLoading(false);
    }
  };

  const sendStickerItem = async (item) => {
    const attachmentIds = getAttachmentIds(item);

    if (!attachmentIds.length) {
      return;
    }

    setLoading(true);
    const payload = {
      attachments: attachmentIds,
      messageType: "sticker",
      content: "",
    };

    if (isGroupChat) {
      payload.groupid = groupId;
    } else {
      payload.touserid = id;
      payload.recipient = id;
    }

    if (replyMessage?.id) {
      payload.replyTo = replyMessage.id;
    }

    try {
      const parsedResponse = await sendWithRetry(payload);

      if (parsedResponse?.status === 1) {
        setChatMessages((prev) => {
          const updated = [...prev, normalizeOutgoingMessage(parsedResponse?.data)];
          localStorage.setItem("chatMessages", JSON.stringify(updated));
          return updated;
        });
        setReplyMessage(null);
        setEmoji(false);
        if (!isGroupChat) {
          fetchUsers();
        }
      }
    } catch (error) {
      console.error("Error during sticker send:", error);
    } finally {
      setLoading(false);
    }
  };

  const resetStickerDraft = () => {
    setStickerTitle("");
    setStickerFile(null);
    setStickerPreviewUrl("");
  };

  const handleStickerFileChange = (event) => {
    const nextFile = event.target.files?.[0] || null;
    setStickerFile(nextFile);
    event.target.value = "";
  };

  const handleSaveSticker = async () => {
    if (!stickerTitle.trim() || !stickerFile) {
      return;
    }

    setSavingSticker(true);

    try {
      const uploadResp = await dispatch(
        uploadFileInChat({ fileupload: [stickerFile] })
      );
      const uploadedStickerId = uploadResp?.payload?.uploads?.[0]?.id;

      if (!uploadedStickerId) {
        return;
      }

      const saveResp = await dispatch(
        addQuickReply({
          title: stickerTitle.trim(),
          question: "",
          content: "",
          messageType: "sticker",
          orderBy: stickerItems.length + 1,
          attachments: [uploadedStickerId],
          status: true,
        })
      );

      if (saveResp?.payload?.status === 1) {
        resetStickerDraft();
        setShowStickerManager(false);
        dispatch(getFrontEndQuickReply());
      }
    } finally {
      setSavingSticker(false);
    }
  };

  const handleRemoveSticker = async (event, stickerId) => {
    event.stopPropagation();

    const confirmDelete = window.confirm(
      "Remove this sticker from your sticker pack?"
    );

    if (!confirmDelete) {
      return;
    }

    const response = await dispatch(
      updateQuickReply({
        id: stickerId,
        softDelete: true,
      })
    );

    if (response?.payload?.status === 1) {
      dispatch(getFrontEndQuickReply());
    }
  };

  // Handle form submit
  const handleSubmit = async(e) => {
    setLoading(true);
    e.preventDefault();
    const filesToUpload = getFilesForUpload(fileupload);

    if (!filesToUpload.length && !message.trim()) {
      setLoading(false);
      return;
    }

    const payload = {
      messageType: detectMessageType(filesToUpload),
      content:  message,
    };
    if (isGroupChat) {
      payload.groupid = groupId;
    } else {
      payload.touserid = id;
      payload.recipient = id;
    }
    if (replyMessage?.id) {
      payload.replyTo = replyMessage.id;
    }
  if(filesToUpload?.length){
    localStorage.setItem("fileupload", 0);
    const apiResp = await dispatch(uploadFileInChat({
      fileupload: filesToUpload
    }))
    if(apiResp?.payload?.status){
      const uploadIds = apiResp?.payload?.uploads?.map(upload => upload.id) || [];
      const extendedPayload = {
        ...payload,
        attachments: uploadIds,
      };
      sendWithRetry(extendedPayload)
      .then((parsedResponse) => {
        if(parsedResponse?.message === "Message read successfully."){
          localStorage.clear();
          navigate("/");
        }
        setChatMessages((prev) => {
          const updated = [...prev, normalizeOutgoingMessage(parsedResponse?.data)];
          localStorage.setItem("chatMessages", JSON.stringify(updated));
          return updated;
        });
        if (parsedResponse?.status === 1) {
          clearComposer();
          setLoading(false);
          fetchUsers();
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error during socket emit:", error);
        setLoading(false);
      });
      inputRef.current?.focus();
      inputRef.current?.focus();
      localStorage.setItem("fileupload", 1);
    }
  }else{
    sendWithRetry(payload)
      .then((parsedResponse) => {
        setChatMessages((prev) => {
          const updated = [...prev, normalizeOutgoingMessage(parsedResponse?.data)];
          localStorage.setItem("chatMessages", JSON.stringify(updated));
          return updated;
        });
        if (parsedResponse?.status === 1) {
          clearComposer();
          setLoading(false);
        } else {
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error during socket emit:", error);
        setLoading(false);
      });
      inputRef.current?.focus();
  }
  };
  
  return (
    <>
      {socketReady === false && (
        <div className="chat-offline-banner">
          <i className="bi bi-wifi-off me-2"></i>
          Reconnecting… your message will be sent automatically when connection is restored.
        </div>
      )}
      {/* {localStorage.getItem("slug") === "team_leader" && (
        <QuickReplyTab handleSubmit={handleSubmit} id={id} />
      )} */}
      <>
        {replyMessage && (
          <ReplyPreview
            replyTo={replyMessage}
            title="Replying to"
            className="chat-composer-reply"
            onClose={() => setReplyMessage(null)}
          />
        )}
        {showMentions && filteredMentions.length > 0 && (
          <div className="chat-mention-list">
            {filteredMentions.map((member, idx) => (
              <button
                key={member.id}
                type="button"
                className={`chat-mention-item${idx === mentionHighlightIdx ? " active" : ""}`}
                onMouseDown={(e) => { e.preventDefault(); insertMention(member); }}
                onMouseEnter={() => setMentionHighlightIdx(idx)}
              >
                <span className="chat-mention-avatar">
                  {member.name.charAt(0).toUpperCase()}
                </span>
                <span className="chat-mention-name">@{member.name}</span>
              </button>
            ))}
          </div>
        )}
        {showSlashReplies && (
          <div className="chat-slash-replies">
              {slashReplyItems
              ?.filter((item) => {
                const slashQuery = message.trim().slice(1).toLowerCase();
                if (!slashQuery) return true;
                const searchableText = [item?.title, item?.question, item?.content]
                  .filter(Boolean)
                  .join(" ")
                  .toLowerCase();
                return searchableText.includes(slashQuery);
              })
              ?.map((item) => (
                <button
                  key={item?.id}
                  type="button"
                  className="chat-slash-reply-item"
                  onClick={() => sendQuickReplyItem(item)}
                >
                  <strong>{item?.title || "Quick Reply"}</strong>
                  <span>{item?.content || item?.question || "Attachment reply"}</span>
                </button>
              ))}
          </div>
        )}
        {audioPreview?.url && (
          <Row className="align-items-center">
            <Col md={10} sm={10} xs={10}>
              <audio controls src={audioPreview.url} />
            </Col>
            <Col md={2} sm={2} xs={2}>
              <span
                onClick={clearAudioPreview}
                className="bg-danger p-2 text-white rounded-circle d-flex align-items-center justify-content-center"
                style={{ cursor: "pointer" }}
              >
                <i className="bi bi-trash3"></i>
              </span>
            </Col>
          </Row>
        )}
        <Row className="bg-white">
          {attachmentPreviews.map((file) => (
            <Col md={2} sm={2} xs={2} className="text-center" key={file.id}>
              {file.type.startsWith("image/") ? (
                <img
                  src={file.url}
                  alt="attachment preview"
                  className="w-100"
                />
              ) : file.type.startsWith("video/") ? (
                <video width="100%" controls>
                  <source src={file.url} type={file.type} />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center p-2 border rounded" style={{ minHeight: 60 }}>
                  <i className="bi bi-file-earmark-fill fs-3 text-secondary"></i>
                  <small className="text-truncate w-100 text-center mt-1" style={{ fontSize: 10 }}>
                    {file.name || "File"}
                  </small>
                </div>
              )}

              <span
                onClick={() => handleDeleteAttachment(file.id)}
                className="bg-danger p-1 text-white rounded-circle d-inline-block mt-1"
              >
                <i className="bi bi-trash3"></i>
              </span>
            </Col>
          ))}
        </Row>
      </>
      {showEmojiPicker && (
        <div className="chat-emoji-picker">
          {EMOJI_LIST.map((em) => (
            <button
              key={em}
              type="button"
              className="chat-emoji-btn"
              onClick={() => insertEmoji(em)}
            >
              {em}
            </button>
          ))}
        </div>
      )}
      <Form
        onSubmit={handleSubmit}
        className="border rounded bg-light mb-1 chat-input-box"
      >
        <InputGroup className="flex-wrap-nowrap">
          {isTeamLeader && (
            <Button
              onClick={() => setEmoji(!emoji)}
              variant="outline-secondary"
              className="border-none"
              title="Stickers"
            >
              {emoji ? (
                <i className="bi bi-x-lg"></i>
              ) : (
                <i className="bi bi-sticky-fill"></i>
              )}
            </Button>
          )}
          <Form.Control
            as="textarea"
            rows={1}
            className="py-2 chat-input-textarea"
            placeholder="Type a message..."
            value={message}
            onChange={handleChange}
            onPaste={handlePaste}
            onKeyDown={handleInputKeyDown}
            ref={inputRef}
          />
          {/* Emoji Button */}
          <Button
            type="button"
            variant="outline-secondary"
            className="border-none"
            title="Emoji"
            onClick={() => setShowEmojiPicker((prev) => !prev)}
          >
            <i className="bi bi-emoji-smile"></i>
          </Button>
          {/* Image Upload */}
          <Button
            className="pt-3"
            variant="outline-secondary"
            as="label"
            htmlFor="image-upload"
          >
            <i className="bi bi-image"></i>
          </Button>
          <input
            className="py-2"
            type="file"
            multiple
            id="image-upload"
            style={{ display: "none" }}
            onChange={handleImageChange}
          />
          {/* Audio Recording */}
          <Button
            variant={isRecording ? "danger" : "outline-secondary"}
            onClick={isRecording ? stopRecording : startRecording}
            style={{ minWidth: 48, minHeight: 48 }}
          >
            <i
              className={`bi ${isRecording ? "bi-stop-circle" : "bi-mic"}`}
            ></i>
          </Button>
          {/* Send Button */}
          <Button
            variant="success"
            className="send-message-button"
            type="submit"
            disabled={loading ? true : false}
          >
            {loading ? <Spinner /> : <i className="bi bi-send"></i>}
          </Button>
        </InputGroup>
        {/* Emoji Picker */}
        {emoji && isTeamLeader && (
          <div className="chat-sticker-picker border rounded">
            <div className="chat-sticker-picker-header">
              <div>
                <strong>My Stickers</strong>
                <span>{stickerItems.length ? "Tap to send instantly" : "Add your first sticker"}</span>
              </div>
              <Button
                type="button"
                size="sm"
                variant="success"
                onClick={() => setShowStickerManager(true)}
              >
                <i className="bi bi-plus-lg me-1"></i>
                Add
              </Button>
            </div>
            {stickerItems.length > 0 ? (
              <div className="chat-sticker-grid">
                {stickerItems.map((item) => (
                  <div key={item?.id} className="chat-sticker-card">
                    <button
                      type="button"
                      className="chat-sticker-card-button"
                      onClick={() => sendStickerItem(item)}
                      title={item?.title}
                    >
                      <img
                        src={item?.files?.[0]?.cloudUrl}
                        alt={item?.title || "Sticker"}
                      />
                      <span>{item?.title}</span>
                    </button>
                    <button
                      type="button"
                      className="chat-sticker-remove"
                      onClick={(event) => handleRemoveSticker(event, item?.id)}
                      aria-label={`Remove ${item?.title || "sticker"}`}
                    >
                      <i className="bi bi-trash3"></i>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mb-0 text-muted px-2 pb-2">
                Stickers are personal for each team leader. Add one here and it will be ready in this chat box.
              </p>
            )}
          </div>
        )}

        {/* Preview Section */}
        {/* <div className="mt-3">
        {image && (
          <div>
            <p>📷 Image Preview:</p>
            <img src={image} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px' }} />
          </div>
        )}
        {audioPreview?.url && (
          <div>
            <p>🎙️ Audio Preview:</p>
            <audio controls src={audioPreview.url} />
          </div>
        )}
      </div> */}
      </Form>
      <Modal
        show={showStickerManager}
        onHide={() => {
          setShowStickerManager(false);
          resetStickerDraft();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Sticker</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Sticker Name</Form.Label>
            <Form.Control
              type="text"
              placeholder="Example: Welcome sticker"
              value={stickerTitle}
              onChange={(event) => setStickerTitle(event.target.value)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Sticker Image</Form.Label>
            <Form.Control
              type="file"
              accept="image/*"
              onChange={handleStickerFileChange}
            />
            <Form.Text className="text-muted">
              Use a PNG or WebP image for the best sticker result.
            </Form.Text>
          </Form.Group>
          {stickerPreviewUrl && (
            <div className="chat-sticker-upload-preview">
              <img src={stickerPreviewUrl} alt={stickerTitle || "Sticker preview"} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowStickerManager(false);
              resetStickerDraft();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={handleSaveSticker}
            disabled={savingSticker || !stickerTitle.trim() || !stickerFile}
          >
            {savingSticker ? "Saving..." : "Save Sticker"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ChatInput;
